import { useState } from "react";
import { motion } from "framer-motion";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Laptop,
  Users,
  Star,
  ExternalLink,
  Search,
  Filter,
  Building2,
  GraduationCap,
  Landmark,
  ArrowRight,
  DollarSign,
  CheckCircle2,
} from "lucide-react";
import { SOFTWARE_RESOURCES, PARTNERSHIP_OPPORTUNITIES } from "../../data/mockData";

const categoryIcons = {
  Productivity: Laptop,
  CRM: Users,
  Design: Star,
  Communication: Users,
};

const SoftwareCard = ({ software }) => {
  const Icon = categoryIcons[software.category] || Laptop;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border ${
        software.recommended ? "border-violet-200" : "border-zinc-200"
      } p-5 hover:shadow-md transition-all`}
      data-testid={`software-card-${software.id}`}
    >
      {software.recommended && (
        <div className="flex items-center gap-1 text-amber-500 mb-3">
          <Star className="h-3 w-3 fill-amber-500" />
          <span className="text-xs font-medium">Recommended for you</span>
        </div>
      )}

      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
            <Icon className="h-6 w-6 text-violet-600" />
          </div>
          <div>
            <h4 className="font-heading font-semibold text-zinc-900">{software.name}</h4>
            <Badge variant="secondary" className="text-xs mt-1">{software.category}</Badge>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-heading font-bold text-emerald-600">{software.value}</p>
          <p className="text-xs text-zinc-500">value</p>
        </div>
      </div>

      <p className="text-sm text-zinc-600 mb-4">{software.description}</p>

      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-4">
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
        <span>Eligibility: {software.eligibility}</span>
      </div>

      <Button variant="outline" className="w-full" size="sm">
        Apply Now <ExternalLink className="h-3 w-3 ml-2" />
      </Button>
    </motion.div>
  );
};

const PartnershipCard = ({ partnership }) => {
  const typeIcons = {
    Collaboration: Building2,
    Research: GraduationCap,
    "Co-funding": Landmark,
  };
  const Icon = typeIcons[partnership.type] || Building2;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-zinc-200 p-5 hover:border-violet-200 hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
            <Icon className="h-5 w-5 text-violet-600" />
          </div>
          <div>
            <h4 className="font-medium text-zinc-900">{partnership.organization}</h4>
            <p className="text-xs text-zinc-500">{partnership.region}</p>
          </div>
        </div>
        <Badge className="bg-violet-100 text-violet-700">
          {(partnership.alignment * 100).toFixed(0)}% match
        </Badge>
      </div>

      <div className="p-3 bg-zinc-50 rounded-lg mb-4">
        <p className="text-xs font-medium text-zinc-500 mb-1">{partnership.type} Opportunity</p>
        <p className="text-sm text-zinc-700">{partnership.opportunity}</p>
      </div>

      <Button variant="ghost" className="w-full text-violet-600 hover:text-violet-700" size="sm">
        View Details <ArrowRight className="h-3 w-3 ml-1" />
      </Button>
    </motion.div>
  );
};

export const ResourceHubTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");

  const totalSavings = SOFTWARE_RESOURCES.reduce((sum, s) => {
    const value = parseInt(s.value.replace(/[^0-9]/g, ""));
    return sum + value;
  }, 0);

  const filteredSoftware = SOFTWARE_RESOURCES.filter((s) => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = category === "all" || s.category === category;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6" data-testid="resourcehub-tab">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Available Tools</p>
                <p className="text-2xl font-heading font-bold text-zinc-900">500+</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <Laptop className="h-6 w-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Potential Savings</p>
                <p className="text-2xl font-heading font-bold text-emerald-600">${(totalSavings / 1000).toFixed(0)}K+</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Recommended</p>
                <p className="text-2xl font-heading font-bold text-zinc-900">
                  {SOFTWARE_RESOURCES.filter((s) => s.recommended).length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Star className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Partner Matches</p>
                <p className="text-2xl font-heading font-bold text-zinc-900">{PARTNERSHIP_OPPORTUNITIES.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Software Directory */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle>Free Software Directory</CardTitle>
              <CardDescription>AI-curated tools personalized for your organization</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  placeholder="Search tools..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-40">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Productivity">Productivity</SelectItem>
                  <SelectItem value="CRM">CRM</SelectItem>
                  <SelectItem value="Design">Design</SelectItem>
                  <SelectItem value="Communication">Communication</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSoftware.map((software) => (
              <SoftwareCard key={software.id} software={software} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Ecosystem Matchmaker */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Ecosystem Matchmaker</CardTitle>
              <CardDescription>Strategic partnership opportunities matched to your mission</CardDescription>
            </div>
            <Button variant="outline" size="sm">
              Browse All <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {PARTNERSHIP_OPPORTUNITIES.map((partnership) => (
              <PartnershipCard key={partnership.id} partnership={partnership} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResourceHubTab;
